import {
  useEffect,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { api, getPosHeaders } from "./api";
import { usePermissions } from "./PermissionContext";

import "./POSClients.css";


const EMPTY_CUSTOMER = {
  document_type: "CC",
  document_number: "",
  name: "",
  email: "",
  phone: "",
  address: "",
  notes: "",
  is_active: true,
};

const DOCUMENT_TYPES = [
  ["CC", "Cédula de ciudadanía"],
  ["NIT", "NIT"],
  ["CE", "Cédula de extranjería"],
  ["TI", "Tarjeta de identidad"],
  ["PAS", "Pasaporte"],
];


function errorText(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .filter(Boolean)
      .join(". ");
  }

  return error.message || "No fue posible completar la operación.";
}


function CustomerDialog({
  title,
  busy,
  onClose,
  children,
}) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const busyRef = useRef(busy);

  closeRef.current = onClose;
  busyRef.current = busy;

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    dialog.showModal();
    document.body.style.overflow = "hidden";

    function handleCancel(event) {
      event.preventDefault();

      if (!busyRef.current) {
        closeRef.current();
      }
    }

    dialog.addEventListener("cancel", handleCancel);

    return () => {
      dialog.removeEventListener("cancel", handleCancel);

      if (dialog.open) dialog.close();

      document.body.style.overflow = previousOverflow;

      if (previousFocus?.isConnected) {
        previousFocus.focus();
      }
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="pos-clients-dialog"
      aria-labelledby="pos-clients-dialog-title"
      aria-busy={busy}
    >
      <header className="pos-clients-dialog-heading">
        <h2 id="pos-clients-dialog-title">{title}</h2>

        <button
          type="button"
          className="pos-clients-close"
          onClick={onClose}
          disabled={busy}
          aria-label="Cerrar ventana"
        >
          ×
        </button>
      </header>

      {children}
    </dialog>
  );
}


export default function POSClients() {
  const { company } = useParams();

  const {
    can,
    loadingPermissions,
  } = usePermissions();

  const canView = can("customers.view");
  const canManage = can("customers.manage");

  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");

  const [form, setForm] = useState({ ...EMPTY_CUSTOMER });
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [statusCustomer, setStatusCustomer] = useState(null);

  const [reload, setReload] = useState(0);

  const mutationRef = useRef(false);
  const listControllerRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setCustomers([]);
    setShowForm(false);
    setEditingCustomer(null);
    setStatusCustomer(null);
    setModalError("");
    setForm({ ...EMPTY_CUSTOMER });
  }, [company]);

  useEffect(() => {
    const controller = new AbortController();

    listControllerRef.current = controller;

    if (loadingPermissions || !canView) {
      setLoading(loadingPermissions);
      setCustomers([]);

      return () => controller.abort();
    }

    setLoading(true);
    setError("");

    const timer = window.setTimeout(async () => {
      try {
        const { data } = await api.get(
          "/pos/customers/me",
          {
            headers: getPosHeaders(company),
            signal: controller.signal,
            params: {
              search: search.trim() || undefined,
              active_only: activeOnly,
            },
          }
        );

        if (controller.signal.aborted) return;

        if (!Array.isArray(data)) {
          throw new Error(
            "La respuesta del listado de clientes es inválida."
          );
        }

        setCustomers(data);
      } catch (requestError) {
        if (!controller.signal.aborted) {
          setCustomers([]);
          setError(errorText(requestError));
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    company,
    canView,
    loadingPermissions,
    search,
    activeOnly,
    reload,
  ]);

  function refreshCustomers() {
    listControllerRef.current?.abort();
    setReload((current) => current + 1);
  }

  function closeForm() {
    if (mutationRef.current) return;

    setShowForm(false);
    setEditingCustomer(null);
    setModalError("");
    setForm({ ...EMPTY_CUSTOMER });
  }

  function openCreate() {
    if (!canManage || mutationRef.current) return;

    setEditingCustomer(null);
    setForm({ ...EMPTY_CUSTOMER });
    setModalError("");
    setShowForm(true);
  }

  function openEdit(customer) {
    if (!canManage || mutationRef.current) return;

    setEditingCustomer(customer);

    setForm({
      document_type: customer.document_type || "CC",
      document_number: customer.document_number || "",
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      notes: customer.notes || "",
      is_active: customer.is_active,
    });

    setModalError("");
    setShowForm(true);
  }

  function openStatus(customer) {
    if (!canManage || mutationRef.current) return;

    setModalError("");
    setStatusCustomer({
      ...customer,
      targetActive: !customer.is_active,
    });
  }

  function closeStatus() {
    if (mutationRef.current) return;

    setStatusCustomer(null);
    setModalError("");
  }

  function handleChange(event) {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  async function handleSave(event) {
    event.preventDefault();

    if (!canManage || mutationRef.current) return;

    const payload = {
      ...form,
      name: form.name.trim(),
      document_number: form.document_number.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
      notes: form.notes.trim() || null,
    };

    if (
      payload.name.length < 2 ||
      payload.document_number.length < 2
    ) {
      setModalError(
        "El nombre y el documento deben tener al menos dos caracteres."
      );
      return;
    }

    mutationRef.current = true;
    setSaving(true);
    setModalError("");

    try {
      const config = {
        headers: getPosHeaders(company),
      };

      if (editingCustomer) {
        await api.put(
          `/pos/customers/me/${editingCustomer.id}`,
          payload,
          config
        );
      } else {
        await api.post(
          "/pos/customers/me",
          payload,
          config
        );
      }

      if (!mountedRef.current) return;

      toast.success(
        editingCustomer
          ? "Cliente actualizado correctamente."
          : "Cliente registrado correctamente."
      );

      setShowForm(false);
      setEditingCustomer(null);
      setForm({ ...EMPTY_CUSTOMER });

      refreshCustomers();
    } catch (requestError) {
      if (mountedRef.current) {
        setModalError(errorText(requestError));
      }
    } finally {
      mutationRef.current = false;

      if (mountedRef.current) {
        setSaving(false);
      }
    }
  }

  async function handleStatusChange() {
    if (
      !canManage ||
      !statusCustomer ||
      mutationRef.current
    ) {
      return;
    }

    mutationRef.current = true;
    setSaving(true);
    setModalError("");

    try {
      await api.patch(
        `/pos/customers/me/${statusCustomer.id}/status`,
        {
          is_active: statusCustomer.targetActive,
        },
        {
          headers: getPosHeaders(company),
        }
      );

      if (!mountedRef.current) return;

      toast.success(
        statusCustomer.targetActive
          ? "Cliente activado correctamente."
          : "Cliente inactivado correctamente."
      );

      setStatusCustomer(null);
      refreshCustomers();
    } catch (requestError) {
      if (mountedRef.current) {
        setModalError(errorText(requestError));
      }
    } finally {
      mutationRef.current = false;

      if (mountedRef.current) {
        setSaving(false);
      }
    }
  }

  if (loadingPermissions) {
    return <p role="status">Validando permisos…</p>;
  }

  if (!canView) {
    return (
      <div className="alert alert-warning" role="alert">
        No tienes permiso para consultar clientes.
      </div>
    );
  }

  const documentTypes = DOCUMENT_TYPES.some(
    ([value]) => value === form.document_type
  )
    ? DOCUMENT_TYPES
    : [
        ...DOCUMENT_TYPES,
        [form.document_type, form.document_type],
      ];

  return (
    <div className="container-fluid px-0 pos-clients">
      <header className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h1 className="h3">Clientes</h1>

          <p className="text-muted mb-0">
            Consulta, registra y actualiza los clientes de tu empresa.
          </p>
        </div>

        {canManage && (
          <button
            type="button"
            className="pos-clients-primary"
            onClick={openCreate}
            disabled={saving}
          >
            + Nuevo cliente
          </button>
        )}
      </header>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <section className="bg-white border rounded p-3 p-lg-4">
        <div className="d-flex flex-wrap align-items-end gap-3 mb-4">
          <div className="flex-grow-1">
            <label htmlFor="client-search" className="form-label">
              Buscar cliente
            </label>

            <input
              id="client-search"
              className="form-control"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Nombre, documento, teléfono o correo"
            />
          </div>

          <label className="d-flex align-items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(event) =>
                setActiveOnly(event.target.checked)
              }
            />
            Solo activos
          </label>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={refreshCustomers}
            disabled={loading || saving}
          >
            Actualizar
          </button>
        </div>

        {loading ? (
          <p role="status">Consultando clientes…</p>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Documento</th>
                  <th>Correo</th>
                  <th>Teléfono</th>
                  <th>Estado</th>
                  {canManage && <th>Acciones</th>}
                </tr>
              </thead>

              <tbody>
                {customers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canManage ? 6 : 5}
                      className="text-center py-4"
                    >
                      No hay clientes para mostrar.
                    </td>
                  </tr>
                ) : customers.map((customer) => (
                  <tr key={customer.id}>
                    <td>
                      <strong>{customer.name}</strong>
                    </td>

                    <td>
                      {customer.document_type}{" "}
                      {customer.document_number}
                    </td>

                    <td>{customer.email || "—"}</td>
                    <td>{customer.phone || "—"}</td>

                    <td>
                      <span className={`badge ${
                        customer.is_active
                          ? "bg-success"
                          : "bg-secondary"
                      }`}>
                        {customer.is_active ? "Activo" : "Inactivo"}
                      </span>
                    </td>

                    {canManage && (
                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => openEdit(customer)}
                            disabled={saving}
                            aria-label={`Editar a ${customer.name}`}
                          >
                            Editar
                          </button>

                          <button
                            type="button"
                            className={`btn btn-sm ${
                              customer.is_active
                                ? "btn-outline-danger"
                                : "btn-outline-success"
                            }`}
                            onClick={() => openStatus(customer)}
                            disabled={saving}
                            aria-label={`${
                              customer.is_active ? "Inactivar" : "Activar"
                            } a ${customer.name}`}
                          >
                            {customer.is_active ? "Inactivar" : "Activar"}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showForm && canManage && (
        <CustomerDialog
          title={editingCustomer ? "Editar cliente" : "Nuevo cliente"}
          busy={saving}
          onClose={closeForm}
        >
          <p className="text-muted small">
            {editingCustomer
              ? "Actualiza los datos de este cliente."
              : "Completa los datos para registrar un cliente."}
          </p>

          <form onSubmit={handleSave}>
            <fieldset disabled={saving}>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label htmlFor="client-document-type" className="form-label">
                    Tipo de documento
                  </label>

                  <select
                    id="client-document-type"
                    name="document_type"
                    className="form-select"
                    value={form.document_type}
                    onChange={handleChange}
                    autoFocus
                  >
                    {documentTypes.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label htmlFor="client-document" className="form-label">
                    Número de documento
                  </label>

                  <input
                    id="client-document"
                    name="document_number"
                    className="form-control"
                    value={form.document_number}
                    onChange={handleChange}
                    minLength={2}
                    maxLength={40}
                    required
                  />
                </div>

                <div className="col-12">
                  <label htmlFor="client-name" className="form-label">
                    Nombre o razón social
                  </label>

                  <input
                    id="client-name"
                    name="name"
                    className="form-control"
                    value={form.name}
                    onChange={handleChange}
                    minLength={2}
                    maxLength={180}
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label htmlFor="client-email" className="form-label">
                    Correo electrónico
                  </label>

                  <input
                    id="client-email"
                    name="email"
                    type="email"
                    className="form-control"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label htmlFor="client-phone" className="form-label">
                    Teléfono
                  </label>

                  <input
                    id="client-phone"
                    name="phone"
                    type="tel"
                    className="form-control"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12">
                  <label htmlFor="client-address" className="form-label">
                    Dirección
                  </label>

                  <input
                    id="client-address"
                    name="address"
                    className="form-control"
                    value={form.address}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12">
                  <label htmlFor="client-notes" className="form-label">
                    Observaciones
                  </label>

                  <textarea
                    id="client-notes"
                    name="notes"
                    className="form-control"
                    rows={3}
                    value={form.notes}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {modalError && (
                <div className="alert alert-danger mt-3" role="alert">
                  {modalError}
                </div>
              )}

              <div className="pos-clients-modal-actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeForm}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="pos-clients-primary"
                >
                  {saving ? "Guardando…" : "Guardar cliente"}
                </button>
              </div>
            </fieldset>
          </form>
        </CustomerDialog>
      )}

      {statusCustomer && canManage && (
        <CustomerDialog
          title={
            statusCustomer.targetActive
              ? "Activar cliente"
              : "Inactivar cliente"
          }
          busy={saving}
          onClose={closeStatus}
        >
          <p>
            ¿Quieres{" "}
            {statusCustomer.targetActive ? "activar" : "inactivar"} a{" "}
            <strong>{statusCustomer.name}</strong>?
          </p>

          <p className="text-muted small">
            Sus datos e historial se conservarán.
            {!statusCustomer.targetActive && (
              " Dejará de aparecer en las consultas de clientes activos."
            )}
          </p>

          {modalError && (
            <div className="alert alert-danger" role="alert">
              {modalError}
            </div>
          )}

          <div className="pos-clients-modal-actions">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={closeStatus}
              disabled={saving}
              autoFocus
            >
              Cancelar
            </button>

            <button
              type="button"
              className={`btn ${
                statusCustomer.targetActive
                  ? "btn-success"
                  : "btn-danger"
              }`}
              onClick={handleStatusChange}
              disabled={saving}
            >
              {saving
                ? "Guardando…"
                : statusCustomer.targetActive
                  ? "Activar cliente"
                  : "Inactivar cliente"}
            </button>
          </div>
        </CustomerDialog>
      )}
    </div>
  );
}